import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, Clock, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { EventRequestDialog } from "@/components/EventRequestDialog";
import { EventMediaDialog } from "@/components/EventMediaDialog";
import { PromptAndPlateEvent, ApiEventItem, transformApiEventToEvent } from "@/data/promptAndPlateEvents";
import { getPublicImage } from "@/utils/imageUtils";

export const EventsListingSection = () => {
  const [statusFilter, setStatusFilter] = useState<"all" | "upcoming" | "past">("all");
  const [locationFilter, setLocationFilter] = useState<string>("all");
  const [selectedEventForRequest, setSelectedEventForRequest] = useState<PromptAndPlateEvent | null>(null);
  const [selectedEventForMedia, setSelectedEventForMedia] = useState<PromptAndPlateEvent | null>(null);
  const [events, setEvents] = useState<PromptAndPlateEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [availableLocations, setAvailableLocations] = useState<string[]>([]);

  // Fetch content API
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setIsLoading(true);
        // API URL setup
        let apiUrl = "https://api.gimmefy.ai/v2";
        const baseUrl = window.location.origin;
        if (baseUrl.includes("localhost") || baseUrl.includes("dev.gimmefy.ai")) {
          apiUrl = "https://apidev.gimmefy.ai/v2";
        } else if (baseUrl.includes("staging.gimmefy.ai")) {
          apiUrl = "https://apistaging.gimmefy.ai/v2";
        }

        const contentUrl = `${apiUrl}/contents/public?filters[group_name]=WEBSITE_PROMPT_AND_PLATE&sort=display_order`;
        const response = await fetch(contentUrl);
        const data = await response.json();
        console.log("Prompt & Plate API Response:", data);

        if (data.success && Array.isArray(data.payload)) {
          // Include all events: next_session, following_session, upcoming, and past
          // next_session and following_session will show as "upcoming" in the listing
          const cardEvents = data.payload.filter((item: ApiEventItem) => 
            item.tags === "next_session" || item.tags === "following_session" || item.tags === "upcoming" || item.tags === "past"
          );

          // Transform API data to events
          const transformedEvents = cardEvents.map((item: ApiEventItem) => 
            transformApiEventToEvent(item, getPublicImage)
          );

          setEvents(transformedEvents);

          // Extract unique locations for filter
          const locations = Array.from(new Set(transformedEvents.map((e: PromptAndPlateEvent) => e.location))).sort() as string[];
          setAvailableLocations(locations);
        }
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Filter events
  const filteredEvents = events.filter(event => {
    const statusMatch = statusFilter === "all" || event.status === statusFilter;
    const locationMatch = locationFilter === "all" || event.location === locationFilter;
    return statusMatch && locationMatch;
  });
  return <section id="events-listing" className="py-20 px-4">
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight">The Prompt & Plate sessions</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Reserve your seat and check out photos of the attendees from past sessions</p>
        </motion.div>

        {/* Filters */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="flex flex-col gap-3 sm:gap-4 sm:flex-row sm:flex-wrap justify-center mb-12">
          {/* Status Filter */}
          <ToggleGroup type="single" value={statusFilter} onValueChange={value => value && setStatusFilter(value as typeof statusFilter)} className="bg-card border rounded-lg p-1 w-full sm:w-auto">
            <ToggleGroupItem value="all" className="px-3 sm:px-4 flex-1 sm:flex-none data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
              All
            </ToggleGroupItem>
            <ToggleGroupItem value="upcoming" className="px-3 sm:px-4 flex-1 sm:flex-none data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
              Upcoming
            </ToggleGroupItem>
            <ToggleGroupItem value="past" className="px-3 sm:px-4 flex-1 sm:flex-none data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
              Past
            </ToggleGroupItem>
          </ToggleGroup>

          {/* Location Filter */}
          <ToggleGroup type="single" value={locationFilter} onValueChange={value => value && setLocationFilter(value)} className="bg-card border rounded-lg p-1 w-full sm:w-auto">
            <ToggleGroupItem value="all" className="px-3 sm:px-4 flex-1 sm:flex-none data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
              All
            </ToggleGroupItem>
            {availableLocations.map(location => (
              <ToggleGroupItem key={location} value={location} className="px-3 sm:px-4 flex-1 sm:flex-none data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
                {location}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </motion.div>

        {/* Events Grid */}
        {isLoading ? (
          <div className="text-center py-16">
            <p className="text-lg text-muted-foreground">Loading events...</p>
          </div>
        ) : filteredEvents.length > 0 ? <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEvents.map((event, index) => <motion.div key={event.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1 * index
        }} className="group">
                <div className="bg-gradient-to-br from-card to-card/90 backdrop-blur-sm border-2 border-border/50 rounded-2xl p-6 shadow-lg hover:shadow-xl hover:border-primary/30 transition-all duration-300 h-full flex flex-col">
                  {/* Status Badge */}
                  <div className="mb-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${event.status === "upcoming" ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" : "bg-muted text-muted-foreground border border-border"}`}>
                      {event.status === "upcoming" ? "Upcoming" : "Past"}
                    </span>
                  </div>

                  {/* Date & Day */}
                  <div className="mb-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold leading-tight">{event.day}</p>
                        <p className="text-sm text-muted-foreground">{event.date}</p>
                      </div>
                    </div>
                  </div>

                  {/* Time & Location */}
                  <div className="space-y-2 mb-6 flex-grow">
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span>{event.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      <span>{event.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground pl-6">
                      <span>{event.venue}</span>
                    </div>
                  </div>

                  {/* CTA Button */}
                  {event.status === "upcoming" ? <Button size="lg" variant="hero" className="w-full" onClick={() => setSelectedEventForRequest(event)}>
                      Request a seat
                    </Button> : <Button size="lg" variant="outline" className="w-full" onClick={() => setSelectedEventForMedia(event)}>
                      <ImageIcon className="w-4 h-4 mr-2" />
                      View media
                    </Button>}
                </div>
              </motion.div>)}
          </motion.div> : <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="text-center py-16">
            <p className="text-lg text-muted-foreground">
              No events found matching your filters.
            </p>
          </motion.div>}
      </div>

      {/* Request Dialog */}
      {selectedEventForRequest && <EventRequestDialog open={!!selectedEventForRequest} onOpenChange={open => !open && setSelectedEventForRequest(null)} eventDay={selectedEventForRequest.day} eventDate={selectedEventForRequest.date} eventTime={selectedEventForRequest.time} eventLocation={selectedEventForRequest.location} eventVenue={selectedEventForRequest.venue} />}

      {/* Media Dialog */}
      {selectedEventForMedia && selectedEventForMedia.media && <EventMediaDialog open={!!selectedEventForMedia} onOpenChange={open => !open && setSelectedEventForMedia(null)} eventDay={selectedEventForMedia.day} eventDate={selectedEventForMedia.date} media={selectedEventForMedia.media} />}
    </section>;
};